import fileSize from "filesize.js";
import {EMPTY, fromEvent, merge} from "rxjs";
import {filter, map, tap} from "rxjs/operators";
import {SPINNING_ICON} from "./utils";
import {TWHandlePdf} from "./handle_pdf";
import {handleCalculatePrice} from "./handle_calculate_price";

interface HTMLInputEvent extends Event {
  target: HTMLInputElement & EventTarget;
}

class TWFileUploadHandler extends TWHandlePdf {
  events: string[];
  $uploadText: HTMLElement;
  $fileName: HTMLElement;
  $fileSize: HTMLElement;
  $removeIcon: HTMLElement;
  $finalPrice: HTMLInputElement;
  $trackerQuantity: HTMLElement;
  $hiddenPriceInput: HTMLInputElement;
  $hiddenNumberOfPagesInput: HTMLInputElement;

  constructor(
      public $form: HTMLFormElement = null,
      public $dropArea: HTMLElement = null,
      private $fileInput: HTMLInputElement = null,
      public $tracker: HTMLElement = null
  ) {
    super();
    this.events = "drag dragstart dragend dragover dragenter dragleave drop".split(' ');
    if (this.$form) {
      this.$uploadText = this.$dropArea.querySelector(".upload-text");
      this.$fileName = this.$dropArea.querySelector(".upload-file-name");
      this.$fileSize = this.$dropArea.querySelector(".upload-file-size");
      this.$removeIcon = this.$dropArea.querySelector(".upload-remove-icon");
      this.$finalPrice = this.$tracker.querySelector(".final-price");
      this.$trackerQuantity = this.$tracker.querySelector(".final-quantity");
      this.$hiddenPriceInput = this.$form.querySelector('input[id="price"]');
      this.$hiddenNumberOfPagesInput = this.$form.querySelector(
          'input[id="number-of-pages"]'
      );
      this.handleFileDrop();
      this.handleFileChange();
      this.handleDisabledInputs(true);
      this.getPrices();
    }
  }

  async calculatePrice(file?: File) {
    const $form = this.$form;

    // Get the checked input and the select to get the current values
    const $coloreDiStampaInput = $form?.querySelector<HTMLInputElement>('input[name="colore"]:checked');
    const $fronteRetroInput = $form?.querySelector<HTMLInputElement>('input[name="fronteoretro"]:checked');
    const $layoutInput = $form?.querySelector<HTMLInputElement>('input[name="layout"]:checked');
    const $lavorazioneInput = $form?.querySelector<HTMLInputElement>(`input[name="lavorazione"]:checked`);
    const $select = $form && $form.querySelector("select");

    // File from first upload or taken from the input
    const $file = file || this.$fileInput.files[0];

    // DOM initial
    // this.$finalPrice.innerHTML = SPINNING_ICON;

    // We get this from the hidden input so we don't have it to recalculate it every time
    const numberOfPages =
        this.$hiddenNumberOfPagesInput.value || (await this.getNumPages($file));

    // Enable inputs
    // this.handleDisabledInputs(false);

    // Effective number of pages are the total pages divided by the type of the layout and then divided by the fact that
    // the user could print front and back or just front
    const pagesInSheet = Math.ceil(+numberOfPages / +$layoutInput.value);
    const onlyFronte = +$fronteRetroInput.value > 0;
    const effectiveNumberOfPages = onlyFronte ? pagesInSheet : pagesInSheet / 2;

    if (numberOfPages > 0) {
      const costoLavorazione = Number($lavorazioneInput?.value) * effectiveNumberOfPages;
      const finalPrice = handleCalculatePrice(
          $coloreDiStampaInput,
          $fronteRetroInput,
          pagesInSheet,
          $select,
          costoLavorazione
      );

      // Disable the select with the rilegatura depending on the number of pages
      // Also we need to take in account if we are using the fronteRetro
      this.handleDisableSelect(Math.ceil(effectiveNumberOfPages));

      // DOM manipulation
      this.$finalPrice.innerHTML = `${finalPrice}`;

      // Adds price to hidden input every time we calculate it
      this.$hiddenPriceInput.value = `${finalPrice}`;

      return finalPrice;
    }
  }

  getPrices() {
    const $form = this.$form;
    const $inputs = $form && $form.querySelectorAll<HTMLInputElement>("input");
    const $select = $form && $form.querySelector("select");

    const inputs$ = $inputs
        ? fromEvent<MouseEvent>($inputs, "change", {capture: false}).pipe(
            map<MouseEvent, HTMLInputElement>(e => e.currentTarget as HTMLInputElement),
            filter(({name}) => name !== "tw-form-upload"),
            tap(($input) => {
              const {value = '', name} = $input;
              if (name === "lavorazione") {
                const resetRilegatura = Number(value) > 0;
                const $rilegatura = this.$form?.querySelector<HTMLSelectElement>('select');
                const $rilegatureSection = $rilegatura?.closest?.('.tw-input-section');

                $rilegatureSection?.classList.remove('disabled');

                if (resetRilegatura) {
                  $rilegatura.value = 'nessuna';
                  $rilegatura.options.selectedIndex = 1;
                  $rilegatureSection?.classList.add('disabled');
                }
              }
            }),
            tap(() => this.calculatePrice(null))
        )
        : EMPTY;

    const select$ = $select
        ? fromEvent($select, "change").pipe(tap(() => this.calculatePrice()))
        : EMPTY;

    merge(inputs$, select$).subscribe();
  }

  async handleFileUpload(file: File) {
    // DOM initial
    this.$fileSize && (this.$fileSize.innerHTML = SPINNING_ICON);
    this.$removeIcon && (this.$removeIcon.style.display = "none");
    this.$fileName && (this.$fileName.innerText = "");
    this.$uploadText &&
    (this.$uploadText.innerHTML = "Calcolo pagine in corso...");
    this.$trackerQuantity.innerHTML = SPINNING_ICON;
    this.handleDisabledInputs(true);

    // Inherited from extended class
    const numberOfPages = await this.getNumPages(file);

    // Save number of pages until next upload
    numberOfPages &&
    (this.$hiddenNumberOfPagesInput.value = numberOfPages.toString());

    const {name: fileName, size} = file;
    const calculatedFileSize = fileSize(size);
    this.$tracker.classList.add("--file-uploaded");
    this.$dropArea.classList.add("--file-uploaded");

    // Disable input if PDF has only one page
    this.handleOnePagePDF(numberOfPages);

    // Calculate price after upload
    this.calculatePrice(file);

    // DOM updates
    this.handleDisabledInputs(false);

    this.$fileName && (this.$fileName.innerHTML = fileName);
    this.$fileSize && (this.$fileSize.innerHTML = `(${calculatedFileSize})`);
    this.$removeIcon && (this.$removeIcon.style.display = "block");
    numberOfPages > 0 &&
    (this.$trackerQuantity.innerHTML = numberOfPages.toString());
    this.$uploadText &&
    (this.$uploadText.innerHTML = `Pagine documento: <strong>${
        numberOfPages ?? ""
    }</strong>`);
  }

  handleFileChange() {
    this.$fileInput &&
    fromEvent(this.$fileInput, "change").subscribe((e: HTMLInputEvent) => {
      if (!e.target.files.length) {
        return;
      }
      const [file] = Object.values(e.target.files);
      this.handleFileUpload(file);
    });
  }

  handleFileDrop() {
    if (!this.$dropArea) {
      return;
    }

    this.events.forEach((eventName) =>
        fromEvent(this.$dropArea, eventName).subscribe((e: Event) =>
            e.preventDefault()
        )
    );

    return;

    // fromEvent(this.$dropArea, "dragover").subscribe(() =>
    //     this.$dropArea.classList.add("drag-over")
    // );
    // fromEvent(this.$dropArea, "dragleave").subscribe(() =>
    //     this.$dropArea.classList.remove("drag-over")
    // );
    //
    // fromEvent(this.$dropArea, "drop").subscribe((e: DragEvent) => {
    //   this.$dropArea.classList.remove("drag-over");
    //   const dt = e.dataTransfer;
    //   this.handleFileUpload(dt.files[0]);
    // });
  }

  handleDisableSelect(numberOfPages: number) {
    const $select = this.$form.querySelector("select") as HTMLSelectElement;
    const selectedOption = $select.options[$select.selectedIndex];

    if (!$select) {
      return;
    }

    const selectOptions = $select.options;
    if ($select) {
      Array.from(selectOptions).forEach((option) => {

        const {maxPages = '0', minPages = '0'} = option.dataset;

        // If the min/maxPages is 0, it means that the option is always available
        if (maxPages === "0" && minPages === "0") {
          option.disabled = false;
          option.style.display = "block";
          return;
        }

        const shouldDisableOption = numberOfPages > +maxPages || numberOfPages < +minPages;

        option.disabled = shouldDisableOption;
        option.style.display = shouldDisableOption ? "none" : 'block';
      });
    }

    setTimeout(() => {
      if (selectedOption?.hasAttribute?.("disabled")) {
        $select.selectedIndex = 1;
      }
    }, 0);
  }

  handleOnePagePDF(numberOfPages: number) {
    const $fronteRetroInput =
        this.$form &&
        (this.$form.querySelector("#fronte_retro") as HTMLInputElement);
    const $soloFronteInput =
        this.$form &&
        (this.$form.querySelector("#solo_fronte") as HTMLInputElement);

    const isSinglePage = numberOfPages === 1;

    $fronteRetroInput.disabled = isSinglePage;
    $fronteRetroInput.checked = !isSinglePage;
    $fronteRetroInput.classList[isSinglePage ? "add" : "remove"](
        "--forcefully-disabled"
    );
    $soloFronteInput.checked = isSinglePage;

    $fronteRetroInput.closest("label").style.opacity = isSinglePage
        ? "0.4"
        : "1";
  }
}

window.addEventListener("DOMContentLoaded", () => {
  const $form = document.querySelector("#tw-print-form") as HTMLFormElement;
  const $dropArea = document.querySelector(".tw-file-drop-area") as HTMLElement;
  const $fileInput = document.querySelector(
      ".tw-form-file-upload"
  ) as HTMLInputElement;
  const $tracker = document.querySelector("#tw_form_tracker") as HTMLElement;
  new TWFileUploadHandler($form, $dropArea, $fileInput, $tracker);
});
